/**
 * Centralized Data Validation & Normalization Layer
 * 
 * CHOKE POINT STRATEGY:
 * All API responses and SSE data MUST pass through this layer before entering React state.
 * Once data passes through here, components can trust it and render directly without guards.
 * 
 * Philosophy: Validate at the boundary, trust in the core.
 */

// ============================================================================
// PRIMITIVE VALIDATORS (Building Blocks)
// ============================================================================

export const toSafeString = (v: unknown, fallback = ''): string => {
  if (v == null) return fallback
  if (typeof v === 'string') return v
  if (typeof v === 'object') {
    // Handle Pydantic validation errors specifically
    if ('type' in v && 'loc' in v && 'msg' in v) {
      return `Validation Error: ${(v as { msg?: unknown }).msg || 'Unknown error'}`
    }
    // Handle Axios error objects
    if ('message' in v && 'code' in v) {
      return toSafeString((v as { message?: unknown }).message, fallback)
    }
    // A circular structure makes JSON.stringify throw, and this function's
    // whole contract is that it does not throw. Caught here rather than
    // avoided upstream: the input is whatever the network handed us.
    try {
      return JSON.stringify(v, null, 2)
    } catch {
      return '[unserializable object]'
    }
  }
  return String(v)
}

export const toSafeNumber = (v: unknown, fallback = 0): number => {
  if (v == null) return fallback
  // Number(Symbol()) throws rather than returning NaN, the one primitive that
  // does. Everything else (functions, objects, promises) coerces to NaN and
  // falls through to the fallback below.
  if (typeof v === 'symbol') return fallback
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : fallback
}

export const toSafeBoolean = (v: unknown, fallback = false): boolean => {
  if (v == null) return fallback
  if (typeof v === 'boolean') return v
  return !!v
}

export const toSafeArray = <T>(v: unknown, fallback: T[] = []): T[] => {
  return Array.isArray(v) ? v : fallback
}

// ============================================================================
// VIEW MODELS (Normalized Data Structures for UI)
// ============================================================================

export interface SafeFileMetadata {
  id: number
  key: string  // S3 key/path - required for chunking operations
  filename: string
  file_size: number
  file_type: string
  size_mb: number
  directory: string
  last_modified: string | null
  needs_transcoding: boolean
  transcoding_reason: string | null
}

export interface SafeChunkMetadata {
  chunk_number: number
  filename: string
  size_mb: number
  full_path: string
}

export interface SafeFolderMetadata {
  folder_name: string
  full_path: string
  chunk_count: number
  total_size_mb: number
  first_chunk: number
  last_chunk: number
  chunks: SafeChunkMetadata[]
}

export interface SafeStreamProgress {
  status: string
  message: string
  progress: number
  current_chunk: number
  total_chunks: number
  successful_chunks: number
  failed_chunks: number
  error?: string
  chunk_completed: boolean
  timings?: {
    total: number
    download: number
    upload: number
    processing: number
    analysis: number
  }
  type?: string
}

export interface SafeChunkResult {
  chunk_number: number
  status: string
  analysis: string
  summary: string
  attempt: number
  file_size_bytes: number
  s3_key: string
  s3_uri: string
  structured_analysis?: {
    scene_description?: string
    opening_frames?: string
    frame_progression?: string
    closing_frames?: string
    [key: string]: unknown
  }
  timings?: {
    download: number
    upload: number
    processing_wait: number
    processing: number
    analysis: number
    total: number
  }
}

export interface SafeStreamResult {
  stream_id?: string
  stream_prefix?: string
  task_type?: string
  completed_at?: string
  total_chunks?: number
  successful_chunks?: number
  failed_chunks?: number
  total_time_seconds?: number
  avg_time_per_chunk?: number
  chunk_results?: SafeChunkResult[]
  analysis_text?: string  // For AI/LLM analysis results (plain text)
}

export interface SafeAnalysisTask {
  task_id: number
  name: string
  status: string
  task_type: string
  created_time: string
  completed_time: string | null
  error_message: string | null
  result_path: string | null
  upload?: SafeFileMetadata
}

// ============================================================================
// NORMALIZERS (Convert Raw API Data → View Models)
// ============================================================================

type Raw = Record<string, unknown>
const rec = (v: unknown): Raw => (v && typeof v === 'object' ? (v as Raw) : {})

export function normalizeFileMetadata(raw: unknown): SafeFileMetadata {
  const r = rec(raw)
  return {
    id: toSafeNumber(r.id),
    key: toSafeString(r.key || r.file_path || r.filename, ''),  // Preserve S3 key/path for chunking
    filename: toSafeString(r.original_filename || r.filename, 'unknown.file'),  // Prioritize original_filename for display
    file_size: toSafeNumber(r.file_size),
    file_type: toSafeString(r.file_type, 'unknown'),
    size_mb: toSafeNumber(r.size_mb || (r.file_size ? Number(r.file_size) / (1024 * 1024) : 0)),
    directory: toSafeString(r.directory, 'root'),
    last_modified: r.last_modified ? toSafeString(r.last_modified) : null,
    needs_transcoding: toSafeBoolean(r.needs_transcoding, false),
    transcoding_reason: r.transcoding_reason ? toSafeString(r.transcoding_reason) : null,
  }
}

export function normalizeChunkMetadata(raw: unknown): SafeChunkMetadata {
  const r = rec(raw)
  return {
    chunk_number: toSafeNumber(r.chunk_number),
    filename: toSafeString(r.filename, 'unknown.mp4'),
    size_mb: toSafeNumber(r.size_mb),
    full_path: toSafeString(r.full_path, ''),
  }
}

export function normalizeFolderMetadata(raw: unknown): SafeFolderMetadata {
  const r = rec(raw)
  return {
    folder_name: toSafeString(r.folder_name, 'unknown'),
    full_path: toSafeString(r.full_path, ''),
    chunk_count: toSafeNumber(r.chunk_count),
    total_size_mb: toSafeNumber(r.total_size_mb),
    first_chunk: toSafeNumber(r.first_chunk),
    last_chunk: toSafeNumber(r.last_chunk),
    chunks: toSafeArray(r.chunks).map(normalizeChunkMetadata),
  }
}

export function normalizeStreamProgress(raw: unknown): SafeStreamProgress {
  const r = rec(raw)
  return {
    status: toSafeString(r.status, 'processing'),
    message: toSafeString(r.message, ''),
    progress: toSafeNumber(r.progress),
    current_chunk: toSafeNumber(r.current_chunk),
    total_chunks: toSafeNumber(r.total_chunks),
    successful_chunks: toSafeNumber(r.successful_chunks),
    failed_chunks: toSafeNumber(r.failed_chunks),
    error: r.error ? toSafeString(r.error) : undefined,
    chunk_completed: toSafeBoolean(r.chunk_completed),
    timings: r.timings ? (() => { const tm = rec(r.timings); return {
      total: toSafeNumber(tm.total),
      download: toSafeNumber(tm.download),
      upload: toSafeNumber(tm.upload),
      processing: toSafeNumber(tm.processing),
      analysis: toSafeNumber(tm.analysis),
    } })() : undefined,
    type: r.type ? toSafeString(r.type) : undefined,
  }
}

export function normalizeChunkResult(raw: unknown): SafeChunkResult {
  const r = rec(raw)
  return {
    chunk_number: toSafeNumber(r.chunk_number),
    status: toSafeString(r.status, 'queued'),
    analysis: toSafeString(r.analysis, ''),
    summary: toSafeString(r.summary, ''),
    attempt: toSafeNumber(r.attempt),
    file_size_bytes: toSafeNumber(r.file_size_bytes),
    s3_key: toSafeString(r.s3_key, ''),
    s3_uri: toSafeString(r.s3_uri, ''),
    structured_analysis: r.structured_analysis ? (() => { const sa = rec(r.structured_analysis); return {
      scene_description: toSafeString(sa.scene_description, ''),
      opening_frames: toSafeString(sa.opening_frames, ''),
      frame_progression: toSafeString(sa.frame_progression, ''),
      closing_frames: toSafeString(sa.closing_frames, ''),
      ...Object.keys(sa).reduce((acc, key) => {
        if (!['scene_description', 'opening_frames', 'frame_progression', 'closing_frames'].includes(key)) {
          acc[key] = toSafeString(sa[key], '')
        }
        return acc
      }, {} as Record<string, string>)
    } })() : undefined,
    timings: r.timings ? (() => { const tm = rec(r.timings); return {
      download: toSafeNumber(tm.download),
      upload: toSafeNumber(tm.upload),
      processing_wait: toSafeNumber(tm.processing_wait),
      processing: toSafeNumber(tm.processing),
      analysis: toSafeNumber(tm.analysis),
      total: toSafeNumber(tm.total),
    } })() : undefined,
  }
}

export function normalizeStreamResult(raw: unknown): SafeStreamResult {
  const r = rec(raw)
  return {
    stream_id: r.stream_id ? toSafeString(r.stream_id) : undefined,
    stream_prefix: toSafeString(r.stream_prefix, ''),
    task_type: r.task_type ? toSafeString(r.task_type) : undefined,
    completed_at: r.completed_at ? toSafeString(r.completed_at) : undefined,
    total_chunks: toSafeNumber(r.total_chunks),
    successful_chunks: toSafeNumber(r.successful_chunks),
    failed_chunks: toSafeNumber(r.failed_chunks),
    total_time_seconds: toSafeNumber(r.total_time_seconds),
    avg_time_per_chunk: toSafeNumber(r.avg_time_per_chunk),
    chunk_results: toSafeArray(r.chunk_results).map(normalizeChunkResult),
    // For single-file analysis results, preserve analysis_text
    analysis_text: r.analysis_text ? toSafeString(r.analysis_text) : undefined,
  }
}

export function normalizeAnalysisTask(raw: unknown): SafeAnalysisTask {
  const r = rec(raw)
  return {
    task_id: toSafeNumber(r.task_id),
    name: toSafeString(r.name, ''),
    status: toSafeString(r.status, 'unknown'),
    task_type: toSafeString(r.task_type, ''),
    created_time: toSafeString(r.created_time, new Date().toISOString()),
    completed_time: r.completed_time ? toSafeString(r.completed_time) : null,
    error_message: r.error_message ? toSafeString(r.error_message) : null,
    result_path: r.result_path ? toSafeString(r.result_path) : null,
    upload: r.upload ? normalizeFileMetadata(r.upload) : undefined,
  }
}

// ============================================================================
// BULK NORMALIZERS (For Arrays)
// ============================================================================

export function normalizeFileList(rawList: unknown): SafeFileMetadata[] {
  return toSafeArray(rawList).map(normalizeFileMetadata)
}

export function normalizeFolderList(rawList: unknown): SafeFolderMetadata[] {
  return toSafeArray(rawList).map(normalizeFolderMetadata)
}

export function normalizeTaskList(rawList: unknown): SafeAnalysisTask[] {
  return toSafeArray(rawList).map(normalizeAnalysisTask)
}

// ============================================================================
// ERROR NORMALIZER (For Axios/API Errors)
// ============================================================================

// The shape a thrown value might have. Nothing here is asserted: every field is
// optional and read through optional chaining, so a string, null, a DOMException
// or a plain Error all pass through without throwing.
type ThrownShape = {
  response?: { data?: { detail?: unknown } }
  message?: unknown
} | null | undefined

/** The backend's `detail` field from an axios error, or the caller's fallback.
 *
 * This is the shape of ~50 catch blocks across the pages and components:
 * `toast.error(err.response?.data?.detail || 'Failed to X')`. Same behaviour,
 * one place, and the argument is `unknown` rather than `any`, which is what
 * makes the call sites type-safe rather than merely unannotated.
 */
export function apiErrorDetail(error: unknown, fallback: string): string {
  const detail = (error as ThrownShape)?.response?.data?.detail
  return detail ? toSafeString(detail) : fallback
}

export function normalizeErrorMessage(error: unknown, fallback = 'An unknown error occurred'): string {
  const e = error as ThrownShape
  // Check for axios error structure
  if (e?.response?.data?.detail) {
    return toSafeString(e.response.data.detail)
  }
  // Check for standard error object
  if (e?.message) {
    return toSafeString(e.message)
  }
  // Fallback
  return toSafeString(error, fallback)
}

// ============================================================================
// DISCOVERY RESPONSE NORMALIZER
// ============================================================================

export interface SafeDiscoveryResponse {
  total_folders: number
  total_chunks: number
  folders: SafeFolderMetadata[]
}

export function normalizeDiscoveryResponse(raw: unknown): SafeDiscoveryResponse {
  const r = rec(raw)
  return {
    total_folders: toSafeNumber(r.total_folders),
    total_chunks: toSafeNumber(r.total_chunks),
    folders: normalizeFolderList(r.folders),
  }
}

